const HALF_PI = Math.PI * 0.5;
const TAU = Math.PI * 2;

export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function loadSVG(url, targetElement) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const svgText = await response.text();

    if (targetElement) {
      // Insert the SVG content directly into the target element
      targetElement.innerHTML = svgText;
    } else {
      console.error(`Target element with ID '${targetElementId}' not found.`);
    }
  } catch (error) {
    console.error("Error loading SVG:", error);
  }
}

export function isPositionInPath(gElement, gPath, posX, posY) {
  if (!gElement || !gPath) return false;

  const svgPoint = gElement.ownerSVGElement.createSVGPoint();
  svgPoint.x = posX;
  svgPoint.y = posY;

  const ctm = gElement.getScreenCTM().inverse();
  const transformedPoint = svgPoint.matrixTransform(ctm);

  return gPath.isPointInStroke(transformedPoint);
}

export function extractTranslate(transformStr) {
  if (!transformStr) return { x: 0, y: 0 };

  // Match translate(...) with optional commas or spaces
  const match = transformStr.match(
    /translate\(\s*([-\d.]+)[ ,]?\s*([-\d.]*)\s*\)/
  );
  if (!match) return { x: 0, y: 0 };

  const x = parseFloat(match[1]);
  const y = match[2] !== "" ? parseFloat(match[2]) : 0; // if only one number, y = 0
  return { x, y };
}

export function samplePathPoints(
  pathEl,
  step = 2,
  trackCenterlineOffset,
  TRACK_SCALE
) {
  const len = pathEl.getTotalLength();
  const pts = [];
  for (let d = 0; d <= len; d += step) {
    const { x, y } = pathEl.getPointAtLength(d);
    pts.push({
      x: (x + trackCenterlineOffset.x) * TRACK_SCALE,
      y: (y + trackCenterlineOffset.y) * TRACK_SCALE,
    });
  }
  // ensure last point included
  if (
    pts.length === 0 ||
    pts[pts.length - 1][0] !== pathEl.getPointAtLength(len).x
  ) {
    const p = pathEl.getPointAtLength(len);
    pts.push({
      x: (p.x + trackCenterlineOffset.x) * TRACK_SCALE,
      y: (p.y + trackCenterlineOffset.y) * TRACK_SCALE,
    });
  }
  return pts;
}

export function nearestSampleLinear(points, qx, qy) {
  let best = -1,
    bestD2 = Infinity;
  for (let i = 0; i < points.length; i++) {
    const { x, y } = points[i];
    const dx = x - qx,
      dy = y - qy;
    const d2 = dx * dx + dy * dy; // squared distance
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return { index: best, point: points[best], dist: Math.sqrt(bestD2) };
}

export function segmentAngle(points, index) {
  if (points.length < 2) return 0;

  // Handle endpoints gracefully
  const i1 = Math.max(0, index - 1);
  const i2 = Math.min(points.length - 1, index + 1);

  const { x: x1, y: y1 } = points[i1];
  const { x: x2, y: y2 } = points[i2];

  const dx = x2 - x1;
  const dy = y2 - y1;

  return Math.atan2(dy, dx) + HALF_PI;
}

export function directionVectors(angleRad) {
  const c = Math.cos(angleRad - HALF_PI);
  const s = Math.sin(angleRad - HALF_PI);

  return {
    forward: { x: c, y: s },
    backward: { x: -c, y: -s },
    left: { x: s, y: -c },
    right: { x: -s, y: c },
  };
}

export function forwardV(o, distance = 0, angleRad = 0) {
  const dv = directionVectors(angleRad || o.angle || o.heading);
  return {
    x: o.x + dv.forward.x * distance,
    y: o.y + dv.forward.y * distance,
  };
}

export function backwardV(o, distance = 0, angleRad = 0) {
  const dv = directionVectors(angleRad || o.angle || o.heading);
  return {
    x: o.x + dv.backward.x * distance,
    y: o.y + dv.backward.y * distance,
  };
}

export function leftV(o, distance = 0, angleRad = 0) {
  const dv = directionVectors(angleRad || o.angle || o.heading);
  return {
    x: o.x + dv.left.x * distance,
    y: o.y + dv.left.y * distance,
  };
}

export function rightV(o, distance = 0, angleRad = 0) {
  const dv = directionVectors(angleRad || o.angle || o.heading);
  return {
    x: o.x + dv.right.x * distance,
    y: o.y + dv.right.y * distance,
  };
}

export function angleDiffToTarget(ox, oy, headingRad, tx, ty) {
  // vector from object → target
  const dx = tx - ox;
  const dy = ty - oy;

  // absolute angle from object to target (0 = +X axis)
  const targetAngle = Math.atan2(dy, dx) + HALF_PI;

  // difference in [-PI, PI]
  let diff = targetAngle - headingRad;
  diff = ((diff + Math.PI) % (2 * Math.PI)) - Math.PI;

  return diff;
}

export function relativeDirection(cx, cy, headingRad, tx, ty) {
  const dx = tx - cx;
  const dy = ty - cy;

  // transform world delta into local coordinates (relative to heading)
  const cos = Math.cos(headingRad);
  const sin = Math.sin(headingRad);

  const localX = cos * dx + sin * dy; // +X = right of heading
  const localY = -sin * dx + cos * dy; // +Y = forward along heading

  // Determine primary direction
  let dirCode = 0;
  const forward = localY < 0.0001;
  const backward = localY > -0.0001;
  const right = localX > 0.0001;
  const left = localX < -0.0001;

  if (forward) dirCode |= 1; // front
  else if (backward) dirCode |= 2; // back
  if (right) dirCode |= 4; // right
  else if (left) dirCode |= 8; // left

  return dirCode;
}

// Direction bit flags
const DIR_FRONT = 1; // 0001
const DIR_BACK = 2; // 0010
const DIR_RIGHT = 4; // 0100
const DIR_LEFT = 8; // 1000

export function isDirFront(code) {
  return (code & DIR_FRONT) !== 0;
}
export function isDirBack(code) {
  return (code & DIR_BACK) !== 0;
}
export function isDirRight(code) {
  return (code & DIR_RIGHT) !== 0;
}
export function isDirLeft(code) {
  return (code & DIR_LEFT) !== 0;
}

export function formatLapTime(ms) {
  if (ms < 0) return "-:--.---";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000); // 3 digits
  if (isNaN(minutes) || isNaN(seconds) || isNaN(millis)) {
    return "-:--.---";
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${millis
    .toString()
    .padStart(3, "0")}`;
}

export function capValue(value, max = 1) {
  if (value < -max) return -max;
  if (value > max) return max;
  return value;
}

export function calcGeometricRatio(start, end, value) {
  const delta = end - start;
  const e1 = start + delta * Math.floor((value - start) / delta);
  return (value - e1) / delta;
}

export function unwrapAngle(previous, current) {
  let diff = current - previous;
  diff = ((diff + Math.PI) % TAU) - Math.PI;
  return previous + diff;
}

export function arrayBufferToBase64(buf) {
  const u8 = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin);
}

export function base64ToArrayBuffer(b64) {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8.buffer;
}

export async function copyToClipboard(text) {
  // Tier 1: Try modern Clipboard API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true, method: 'modern' };
    } catch (error) {
      console.warn("Clipboard API blocked, trying fallback:", error);
      // Continue to Tier 2
    }
  }

  // Tier 2: Try legacy execCommand method
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);

    textarea.select();
    textarea.setSelectionRange(0, text.length);

    const success = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (success) {
      return { success: true, method: 'execCommand' };
    }
  } catch (error) {
    console.warn("execCommand failed:", error);
  }

  // Tier 3: Return failure signal for manual UI fallback
  return {
    success: false,
    method: 'none',
    text: text
  };
}

export function isInUncontrolledIframe() {
  return window.location.hostname.includes('html-classic.itch.zone');
}
